import { formatUrl, parseUrl, resolveInstanceUrl } from '../instance/urls.ts';
import type { StoryboardCue, StoryboardSpec } from '../model/media.ts';
import { parseError, type ParsedList, type ParseError } from '../result.ts';
import type { ParseContext } from './context.ts';

/**
 * §7 — scrub previews come from the finished WebVTT thumbnail track.
 *
 * Core parses it rather than handing the URL to Shaka's `addThumbnailsTrack()`, and
 * docs/specs/player.md gives
 * the reason: the Shaka route would leave this parser as dead, untested code whose first real
 * exercise is on Android, inverting R12. It also puts the clamp in one place.
 *
 * The clamp is not a nicety. `dQw4w9WgXcQ` is a 214 s video whose sheet grid holds
 * 5 × 25 = 125 tiles while only `count: 108` of them were ever rendered — so the VTT emits 125
 * cues and the last 18 point at blank space. Cutting at `min(duration, count × interval)` is what
 * keeps the scrub preview from going white near the end.
 */

export interface StoryboardCueOptions {
  /** From `VideoDetails.storyboard`. Supplies `count × interval`, and the tile size fallback. */
  readonly spec?: StoryboardSpec | null;
  /** `lengthSeconds`. The other half of the clamp. */
  readonly durationSec?: number | null;
}

const TIMING = /^\s*(\S+)\s+-->\s+(\S+)/;
const TIMESTAMP = /^(?:(\d+):)?(\d{1,2}):(\d{2})(?:[.,](\d{1,3}))?$/;

/** WebVTT allows `HH:MM:SS.mmm` and `MM:SS.mmm`; some writers emit a comma decimal separator. */
function parseVttTimeMs(text: string): number | null {
  const match = TIMESTAMP.exec(text.trim());
  if (!match) return null;
  const [, h, m, s, frac] = match;
  const minutes = Number(m);
  const seconds = Number(s);
  if (minutes > 59 || seconds > 59) return null;
  const millis = frac === undefined ? 0 : Number(frac.padEnd(3, '0'));
  return ((Number(h ?? 0) * 60 + minutes) * 60 + seconds) * 1000 + millis;
}

interface TileRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** `#xywh=0,0,158,90`. Note the declared tile width is 158 while the grid pitch is 160 — as given. */
function parseXywh(fragment: string): TileRect | null {
  const match = /(?:^|[&;])xywh=(-?\d+),(-?\d+),(\d+),(\d+)/.exec(fragment);
  if (!match) return null;
  return { x: Number(match[1]), y: Number(match[2]), w: Number(match[3]), h: Number(match[4]) };
}

/**
 * `count × interval` and the video length, whichever is shorter. `null` when neither is known —
 * an unclamped track is still better than no track, and the trailing tiles are merely blank.
 */
function limitMs(options: StoryboardCueOptions): number | null {
  const candidates: number[] = [];

  const duration = options.durationSec;
  if (duration !== undefined && duration !== null && duration > 0) candidates.push(duration * 1000);

  const spec = options.spec;
  if (spec !== undefined && spec !== null && spec.count !== null && spec.intervalMs !== null) {
    if (spec.count > 0 && spec.intervalMs > 0) candidates.push(spec.count * spec.intervalMs);
  }

  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

function cueBlocks(vtt: string): readonly string[] {
  return vtt
    .replace(/^\ufeff/, '')
    .replace(/\r\n|\r/g, '\n')
    .split(/\n{2,}/);
}

/**
 * R4 — total. A malformed cue drops itself and stays counted; cues past the clamp are **not**
 * counted as drops, because discarding them is the point rather than a failure.
 */
export function parseStoryboardVtt(
  vtt: string,
  ctx: ParseContext,
  options: StoryboardCueOptions = {},
): ParsedList<StoryboardCue> {
  const items: StoryboardCue[] = [];
  const dropped: ParseError[] = [];
  const limit = limitMs(options);
  const spec = options.spec ?? null;

  for (const block of cueBlocks(vtt)) {
    const lines = block.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) continue;

    const timingIndex = lines.findIndex((line) => line.includes('-->'));
    if (timingIndex === -1) continue; // WEBVTT header, NOTE, STYLE, REGION — not a cue.

    const timing = TIMING.exec(lines[timingIndex] ?? '');
    const startMs = timing === null ? null : parseVttTimeMs(timing[1] ?? '');
    const endMs = timing === null ? null : parseVttTimeMs(timing[2] ?? '');
    if (startMs === null || endMs === null || endMs <= startMs) {
      dropped.push(parseError('StoryboardCue', 'timing', 'malformed'));
      continue;
    }

    const payload = lines.slice(timingIndex + 1).join('').trim();
    if (payload === '') {
      dropped.push(parseError('StoryboardCue', 'url', 'missing'));
      continue;
    }

    const parts = parseUrl(payload);
    if (parts === null) {
      dropped.push(parseError('StoryboardCue', 'url', 'malformed'));
      continue;
    }

    // R2 — the sheets are served by the instance (`/sb/…`), so they go through transport like
    // every other fetchable URL. A foreign origin returns `null` and the cue drops rather than
    // leaking a Google host into the model.
    const imageUrl = resolveInstanceUrl(ctx.config, formatUrl({ ...parts, fragment: '' }));
    if (imageUrl === null) {
      dropped.push(parseError('StoryboardCue', 'url', 'malformed'));
      continue;
    }

    const rect = parseXywh(parts.fragment) ?? {
      // No fragment means one image per cue: the whole sheet is the tile.
      x: 0,
      y: 0,
      w: spec?.width ?? 0,
      h: spec?.height ?? 0,
    };

    if (limit !== null && startMs >= limit) continue;
    items.push({ startMs, endMs: limit === null ? endMs : Math.min(endMs, limit), imageUrl, ...rect });
  }

  return { items, dropped };
}

/**
 * How much video the track covers — the last cue's end, since {@link parseStoryboardVtt} already
 * clamped the over-emitted tail. The grid preview's denominator (2026-08-02): its bar shows
 * `position / span`, and a scrub at fraction `f` lands at `f × span`. `0` when there is no track,
 * which every caller treats as "no preview".
 */
export function storyboardSpanMs(cues: readonly StoryboardCue[]): number {
  return cues.length === 0 ? 0 : (cues[cues.length - 1]?.endMs ?? 0);
}

/**
 * The cue covering `positionMs`, or `null`. Binary search rather than a scan, because the player
 * calls this on every pointer move along the scrub bar.
 *
 * Hover time past the end of the track answers with the **last** cue rather than nothing: the
 * clamp already removed the blank tiles, so the alternative is a preview that vanishes in the last
 * few seconds of every video.
 */
export function storyboardCueAt(
  cues: readonly StoryboardCue[],
  positionMs: number,
): StoryboardCue | null {
  if (cues.length === 0) return null;
  if (positionMs < (cues[0]?.startMs ?? 0)) return cues[0] ?? null;

  let low = 0;
  let high = cues.length - 1;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if ((cues[mid]?.startMs ?? 0) <= positionMs) low = mid;
    else high = mid - 1;
  }
  return cues[low] ?? null;
}
